import { CheckIcon, UserCircleIcon } from '@phosphor-icons/react';

import Link from '@/components/ui/Link';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { SPACE_KIND_LABELS, spaceHref, spaceIcon } from '@/lib/spaces/navigation';
import { track } from '@/lib/telemetry';
import type { SpaceSummary } from '@/types/spaces';

export function PersonalScopeMenuItem() {
  const { scope } = useSpaceScope();
  const handleSelect = () => {
    if (scope === 'space') track('scope.switched', { to: 'personal' });
  };

  return (
    <DropdownMenuItem asChild className="cursor-pointer">
      <Link href="/" onClick={handleSelect} className="gap-2.5 rounded-lg p-2">
        <UserCircleIcon aria-hidden className="size-6" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Pessoal</div>
          <p className="text-xs text-text-secondary">Só você vê</p>
        </div>
        {scope === 'personal' && <CheckIcon aria-label="Selecionado" />}
      </Link>
    </DropdownMenuItem>
  );
}

function SpaceScopeMenuItem({ entry }: { entry: SpaceSummary }) {
  const { space } = useSpaceScope();
  const Icon = spaceIcon(entry.kind);
  const handleSelect = () => {
    if (space?.id !== entry.id) track('scope.switched', { to: 'space' });
  };

  return (
    <DropdownMenuItem asChild className="cursor-pointer">
      <Link href={spaceHref(entry.id)} onClick={handleSelect} className="gap-2.5 rounded-lg p-2">
        <Icon aria-hidden className="size-6 shrink-0 text-text-secondary" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{entry.name}</div>
          <p className="text-xs text-text-secondary tabular-nums">
            {entry.memberCount} {entry.memberCount === 1 ? 'pessoa' : 'pessoas'} · {SPACE_KIND_LABELS[entry.kind]}{entry.archivedAt ? ' · Encerrado' : ''}
          </p>
        </div>
        {space?.id === entry.id && <CheckIcon aria-label="Selecionado" />}
      </Link>
    </DropdownMenuItem>
  );
}

function renderSpaceEntry(entry: SpaceSummary) {
  return <SpaceScopeMenuItem key={entry.id} entry={entry} />;
}

export function SpaceScopeMenuItems() {
  const { spaces, listLoading, listError, retryList } = useSpaceScope();

  if (listLoading) {
    return (
      <div role="status" aria-label="Carregando espaços" aria-busy="true" className="space-y-2 p-2">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    );
  }
  if (listError) {
    return (
      <>
        <p role="alert" className="p-2 text-xs text-text-secondary">Não foi possível carregar seus espaços.</p>
        <DropdownMenuItem onSelect={retryList} className="cursor-pointer">Tentar novamente</DropdownMenuItem>
      </>
    );
  }
  if (spaces.length === 0) {
    return <p className="p-2 text-xs text-text-secondary">Você ainda não participa de um espaço.</p>;
  }
  return spaces.map(renderSpaceEntry);
}
